import { getOrganizationUrlFromSlug } from "./getOrganizationUrlFromSlug";

/**
 * Build a URL by replacing route parameters with actual values and optionally appending query params.
 *
 * @param route - The route template (e.g., "/settings/offices/:officeId")
 * @param params - Optional object mapping parameter names to values (e.g., { officeId: "123" })
 * @param options - Optional configuration object
 * @param options.queryParams - Query parameters to append (e.g., { activeTab: "org-integrations" })
 * @param options.fullUrl - Configuration for generating a full URL with domain
 * @param options.fullUrl.slug - Organization slug to prefix the domain (e.g., "acme" -> "https://acme.gettactic.com")
 * @returns The URL with parameters replaced and query string appended
 *
 * @example
 * buildUrl(SETTINGS_ROUTES.facilitiesBuildingGeneral, { officeId: "abc-123" })
 * // Returns: "/settings/offices/abc-123"
 *
 * @example
 * buildUrl(SETTINGS_ROUTES.integrations, undefined, { queryParams: { activeTab: "org-integrations" } })
 * // Returns: "/settings/integrations?activeTab=org-integrations"
 *
 * @example
 * buildUrl(SETTINGS_ROUTES.facilities, undefined, { fullUrl: { slug: "acme" } })
 * // Returns: "https://acme.gettactic.com/settings/offices"
 */
export function buildUrl<T extends Record<string, string>>(
  route: string,
  params?: T,
  options?: {
    queryParams?: Record<string, string | number | boolean>;
    fullUrl?: {
      slug?: string;
    };
  }
): string {
  let url = route;

  // Replace route parameters
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      url = url.replace(`:${key}`, value);
    }
  }

  // Prepend full domain if requested
  if (options?.fullUrl?.slug) {
    url = getOrganizationUrlFromSlug(options.fullUrl.slug, url);
  }

  // Append query parameters
  if (options?.queryParams && Object.keys(options.queryParams).length > 0) {
    const queryString = Object.entries(options.queryParams)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join("&");
    url = `${url}?${queryString}`;
  }

  return url;
}
